import { Markdown } from "tiptap-markdown";

/**
 * Configured tiptap-markdown extension for the composer.
 *
 * Handles bidirectional markdown <-> ProseMirror conversion:
 * - On load: markdown string -> ProseMirror document nodes
 * - On send: ProseMirror document nodes -> markdown string
 *
 * No HTML intermediate step.
 */
export const MarkdownExtension = Markdown.configure({
  html: false,
  tightLists: true,
  tightListClass: "tight",
  bulletListMarker: "-",
  linkify: false,
  breaks: true,
  transformPastedText: true,
  transformCopiedText: true,
});

/**
 * Post-process serialized markdown before storing.
 * - Trims trailing whitespace per line
 * - Collapses 3+ consecutive blank lines to 2
 * - Trims leading/trailing blank lines
 */
export function postProcessMarkdown(md: string): string {
  const result = md
    // Trim trailing whitespace per line
    .replace(/[ \t]+$/gm, "")
    // Collapse 3+ consecutive blank lines to 2
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return result;
}

/**
 * Check if a string contains any markdown syntax.
 * If it's plain text with no formatting, we store it as-is.
 */
export function hasMarkdownSyntax(text: string): boolean {
  // Check for common markdown patterns
  return /(\*\*.+?\*\*|\*.+?\*|~~.+?~~|`.+?`|```[\s\S]*?```|^>\s|^[-*+]\s|^\d+\.\s|\[.+?\]\(.+?\)|^#{1,6}\s)/m.test(
    text,
  );
}

/** Minimal ProseMirror node shape for type-safe doc walking. */
interface PMNode {
  type: { name: string };
  childCount: number;
}

interface PMDoc {
  forEach: (fn: (node: PMNode) => void) => void;
}

/**
 * Get markdown content from the editor, with post-processing.
 * Always returns the markdown serialization to preserve paragraph breaks.
 *
 * prosemirror-markdown drops empty paragraph nodes during serialization,
 * so we walk the ProseMirror document to detect them and re-insert
 * non-breaking space markers (\u00A0) where they were stripped.
 */
export function getEditorMarkdown(editor: {
  storage: Record<string, unknown>;
  getText: () => string;
  state?: { doc: PMDoc };
}): string {
  const mdStorage = editor.storage as {
    markdown?: { getMarkdown?: () => string };
  };
  const markdown = mdStorage.markdown?.getMarkdown?.() ?? editor.getText();
  const processed = postProcessMarkdown(markdown);

  // If we don't have access to the ProseMirror doc, return as-is
  if (!editor.state) return processed;

  // Walk the doc to find empty paragraph nodes that were dropped
  const blockHasContent: boolean[] = [];
  editor.state.doc.forEach((node) => {
    blockHasContent.push(
      !(node.type.name === "paragraph" && node.childCount === 0),
    );
  });

  // No empty paragraphs — nothing was dropped
  if (!blockHasContent.includes(false)) return processed;

  // Re-interleave: split the serialized markdown into content blocks,
  // then insert \u00A0 paragraphs where the serializer dropped empties
  const contentBlocks = processed.split("\n\n");
  const result: string[] = [];
  let ci = 0;
  for (const hasContent of blockHasContent) {
    if (hasContent) {
      result.push(contentBlocks[ci++] ?? "");
    } else {
      result.push("\u00A0");
    }
  }

  return result.join("\n\n").trim();
}
